// src/pages/BuyNowPage/BuyNowPage.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import "./BuyNowPage.css";
import Footer from "../../components/Footer/Footer";
import { useLocation, useNavigate } from "react-router-dom";
import { API, IMAGE_PATH, createPaymentOrder } from "../../services/api";
import { FaCcVisa, FaCcMastercard, FaRupeeSign } from "react-icons/fa";
import pincodes from "../../data/serviceablePincodes.json";
/* ---------------------------------------------------
   Cashfree v3 (CDN) → window.Cashfree({ mode }) → cf.checkout()
   --------------------------------------------------- */
const CF_MODE = process.env.NODE_ENV === "production" ? "production" : "sandbox";

async function loadCashfreeV3() {
  // If the global exists, instantiate and return the SDK instance
  if (window.Cashfree) {
    return await window.Cashfree({ mode: CF_MODE });
  }

  // Inject v3 script (only v3 — do not include any v2 UI scripts)
  await new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error("Failed to load Cashfree v3 SDK"));
    document.head.appendChild(s);
  });

  if (!window.Cashfree) {
    throw new Error("Cashfree v3 did not initialize");
  }

  return await window.Cashfree({ mode: CF_MODE });
}

const PLACEHOLDER = "placeholder.jpg";

const parseImages = (images) => {
  try {
    if (Array.isArray(images)) return images;
    return JSON.parse(images || "[]");
  } catch {
    return [];
  }
};

const asInt = (v, def = 1) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : def;
};

const BuyNowPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { product: passedProduct, variant_id, quantity: passedQty } = state || {};

  const [product, setProduct] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("cashfree");
  const [billingSame, setBillingSame] = useState(true);
  const [mainImage, setMainImage] = useState(PLACEHOLDER);
  // const [quantity, setQuantity] = useState(asInt(state?.quantity, 1));
  const [loading, setLoading] = useState(!passedProduct);
  const [err, setErr] = useState("");
  const [paying, setPaying] = useState(false);
  const [discountCode, setDiscountCode] = useState("");
  const [discountPercent, setDiscountPercent] = useState(0);
  const [discountError, setDiscountError] = useState("");

  const [selectedVariantId, setSelectedVariantId] = useState(
    variant_id || null
  );

  const [quantity, setQuantity] = useState(asInt(passedQty, 1));

  const isSilverProduct = useMemo(() => {
    return String(product?.metal || "").toLowerCase().trim() === "silver";
  }, [product]);

  const handleApplyDiscount = () => {
    if (!isSilverProduct) {
      setDiscountPercent(0);
      setDiscountError("This coupon is valid only on silver jewellery");
      return;
    }

    const code = discountCode.trim().toUpperCase();

    if (code === "GIFTFORHER") {
      setDiscountPercent(20);
      setDiscountError("");
    } else {
      setDiscountPercent(0);
      setDiscountError("Invalid discount code");
    }
  };


  const selectedVariant = useMemo(() => {
    if (!product?.variants?.length) return null;

    return product.variants.find(
      (v) => String(v.id) === String(selectedVariantId)
    ) || null;

  }, [product, selectedVariantId]);


  const availableStock = useMemo(() => {
    // If product has variants
    if (product?.variants?.length > 0) {
      return Number(selectedVariant?.quantity || 0);
    }

    // If no variants → use product quantity
    return Number(product?.quantity || 0);
  }, [product, selectedVariant]);

  // form




  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    address: "",
    apartment: "",
    city: "",
    state: "",
    pin: "",
    phone: "",
  });

  // Cache the Cashfree SDK instance so button click is instant
  const cfRef = useRef(null);
  const [pin, setPin] = useState(formData.pin || "");
  const [pinStatus, setPinStatus] = useState(""); // "", "success", "error", "invalid"
  const serviceableSet = useMemo(() => new Set(pincodes), []);
  // Preload Cashfree v3 once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cf = await loadCashfreeV3();
        if (mounted) cfRef.current = cf;
      } catch (e) {
        console.error("[Cashfree] preload failed:", e);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);





  useEffect(() => {
    if (!pin) {
      setPinStatus("");
      return;
    }

    if (pin.length < 6) {
      setPinStatus("invalid"); // show "enter 6 digits"
      return;
    }

    if (pin.length === 6) {
      setPinStatus(serviceableSet.has(pin) ? "success" : "error");
    }
  }, [pin, serviceableSet]);

 
  useEffect(() => {
    if (availableStock === 0) {
      setQuantity(1);
      return;
    }

    if (quantity > availableStock) {
      setQuantity(availableStock);
    }
  }, [availableStock]);
  // Load product
  // useEffect(() => {
  //   let mounted = true;

  //   const load = async () => {
  //     try {
  //       const pid = String(passedProduct?.id || passedProduct?.product_id || "");
  //       if (!pid) {
  //         navigate("/", { replace: true });
  //         return;
  //       }
  //       const res = await fetch(API.getProductById(pid)); // API.getProductById returns URL
  //       if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
  //       const data = await res.json();
  //       if (!mounted) return;

  //       setProduct(data);
  //       const imgs = parseImages(data.images);
  //       setMainImage(imgs.length ? `${IMAGE_PATH}${imgs[0]}` : PLACEHOLDER);
  //     } catch (e) {
  //       if (!mounted) return;
  //       if (passedProduct) {
  //         setProduct(passedProduct);
  //         const imgs = parseImages(passedProduct.images);
  //         setMainImage(imgs.length ? `${IMAGE_PATH}${imgs[0]}` : PLACEHOLDER);
  //       } else {
  //         setErr("Unable to load product.");
  //       }
  //     } finally {
  //       if (mounted) setLoading(false);
  //     }
  //   };

  //   if (passedProduct) {
  //     setProduct(passedProduct);
  //     const imgs = parseImages(passedProduct.images);
  //     setMainImage(imgs.length ? `${IMAGE_PATH}${imgs[0]}` : PLACEHOLDER);
  //     setLoading(false);
  //   } else {
  //     setLoading(true);
  //     load();
  //   }

  //   return () => {
  //     mounted = false;
  //   };
  // }, [passedProduct, navigate]);

  useEffect(() => {
    let mounted = true;

    const loadProduct = async () => {
      try {
        const pid = String(
          passedProduct?.id ||
          passedProduct?.product_id ||
          ""
        );

        if (!pid) {
          navigate("/", { replace: true });
          return;
        }

        const res = await fetch(API.getProductById(pid));
        if (!res.ok) throw new Error("Failed to fetch product");

        const data = await res.json();
        if (!mounted) return;

        setProduct(data);

        const imgs = parseImages(data.images);
        setMainImage(
          imgs.length ? `${IMAGE_PATH}${imgs[0]}` : PLACEHOLDER
        );

        // ✅ IMPORTANT: restore selected variant from cart
        if (variant_id) {
          setSelectedVariantId(variant_id);
        }

      } catch (err) {
        console.error("Product load error:", err);
        setErr("Unable to load product.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProduct();

    return () => {
      mounted = false;
    };
  }, []);

  // const unitPrice = useMemo(() => {
  //   // If coming from cart → use cart price
  //   if (passedProduct?.unitPrice) {
  //     return Number(passedProduct.unitPrice);
  //   }

  //   // If variant selected → use variant price
  //   if (product?.variants?.length > 0 && passedProduct?.variant_id) {
  //     const matchedVariant = product.variants.find(
  //       (v) => String(v.id) === String(passedProduct.variant_id)
  //     );

  //     if (matchedVariant?.price) {
  //       return Number(matchedVariant.price);
  //     }
  //   }

  //   // Fallback to product price
  //   return Number(product?.price || 0);
  // }, [product, passedProduct])

  const unitPrice = useMemo(() => {
    if (selectedVariant?.price != null) {
      return Number(selectedVariant.price);
    }

    return 0;
  }, [selectedVariant]);



  const subtotal = useMemo(() => unitPrice * quantity, [unitPrice, quantity]);
  
  
  const discountAmount = useMemo(() => {
    if (!isSilverProduct) return 0;
    return Math.round((subtotal * discountPercent) / 100);
  }, [subtotal, discountPercent, isSilverProduct]);

  const finalTotal = useMemo(() => {
    return subtotal - discountAmount;
  }, [subtotal, discountAmount]);
  
  const formatINR = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

useEffect(() => {
  console.log("PRODUCT DATA:", product);
}, [product]);



  useEffect(() => {
    if (!product?.variants?.length) return;

    if (product.variants.length === 1) {
      setSelectedVariantId(product.variants[0].id);
    }
  }, [product]);

  useEffect(() => {
    if (!isSilverProduct) {
      setDiscountPercent(0);
      setDiscountCode("");
      setDiscountError("");
    }
  }, [isSilverProduct]);




  /* ------------------------------- PAY NOW ------------------------------- */
  const handlePayNow = async () => {
    try {

     if (product?.variants?.length > 1 && !selectedVariant) {
  return alert("Please select a valid size.");
}
      if (paymentMethod !== "cashfree") {
        return alert("Only Cashfree flow wired right now.");
      }

      if (!formData.email || !formData.phone || !formData.firstName || !formData.address || !formData.pin) {
        return alert("Please fill Email, Phone, First name, Address, and PIN to continue.");
      }

      const token = localStorage.getItem("token");
      if (!token) return alert("Please log in to continue.");

     
      const amount = Number.isFinite(finalTotal) && finalTotal > 0 ? Number(finalTotal) : 0;
      if (!amount) return alert("Invalid amount");

      console.log("[PAY] 1/3 creating order", { amount, productId: product?.id, quantity });
      setPaying(true);

      // Create order on backend (adds JWT inside createPaymentOrder)
      let resp;
      try {
        resp = await createPaymentOrder({
          amount: finalTotal,  // ✅ send final calculated amount
          customerEmail: formData.email,
          customerPhone: formData.phone,
          customerName: `${formData.firstName} ${formData.lastName}`.trim(),

          productId: product?.id,
          variantId: selectedVariant?.id || null,
          quantity,

          shipping: {
            name: `${formData.firstName} ${formData.lastName}`.trim(),
            phone: formData.phone,
            line1: formData.address,
            line2: formData.apartment,
            city: formData.city,
            state: formData.state,
            pincode: formData.pin,
            country: "India",
          },
        });
      } catch (e) {
        // ✅ Axios style
        const status = e?.response?.status;
        const message =
          e?.response?.data?.error || e?.response?.data?.message || e?.message;
        if (status === 401) {
          alert("You have to login to place an order.");
          navigate("/login"); // change route if your login page is different
          return;
        }

        alert(e?.response?.data?.error || e?.message || "Payment init failed");
        return;
      }
      const { payment_session_id } = resp.data;

      
      // console.log("[PAY] 2/3 backend OK", resp.status, resp.data);
      if (!payment_session_id) throw new Error("No payment_session_id returned");

      // Ensure SDK is ready (fallback if preload didn’t finish)
      if (!cfRef.current) {
        cfRef.current = await loadCashfreeV3();
      }

      // Hosted checkout redirect (opens Cashfree page)
      await cfRef.current.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_self", // or "_blank" for new tab
      });

      console.log("[PAY] 3/3 redirected to Cashfree");
      // After payment, Cashfree will send user to RETURN_URL; webhook confirms PAID & updates stock.
    } catch (err) {
      console.error("[PAY][GENERIC]", err);
      alert(err?.message || "Payment init failed");
    } finally {
      setPaying(false);
    }
  };

  if (loading) return <p style={{ padding: 16 }}>Loading…</p>;
  if (err) return <p style={{ padding: 16, color: "crimson" }}>{err}</p>;
  if (!product) return <p style={{ padding: 16 }}>No product selected.</p>;
  // const availableStock = Number(product?.quantity) || 0;



  return (
    <>
      <div className="buynow-wrapper">
        <div className="buynow-container">
          {/* Left Section */}
          <div className="buynow-left-section">
            <h2 className="buynow-section-title">Email</h2>
            <input
              type="text"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="Add your Email"
              className="buynow-input contact-input"
            />
            <div className="buynow-checkbox-wrap">
              <input type="checkbox" id="news-offers" />
              <label htmlFor="news-offers">Email me with news and offers</label>
            </div>

            {/* <h2 className="buynow-section-title">Delivery</h2>
            <div className="buynow-delivery-options">
              <label>
                <input type="radio" name="delivery" defaultChecked /> Ship
              </label>
              <label>
                <input type="radio" name="delivery" /> Pickup in store
              </label>
            </div> */}

            <div className="buynow-address-section">
              <select className="buynow-input country-select">
                <option value="India">India</option>
              </select>
              <input
                type="text"
                name="phone"
                placeholder="Phone"
                className="buynow-input"
                value={formData.phone}
                onChange={handleInputChange}
              />
              <div className="buynow-name-fields">
                <input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  className="buynow-input"
                  value={formData.firstName}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  className="buynow-input"
                  value={formData.lastName}
                  onChange={handleInputChange}
                />
              </div>
              <input
                type="text"
                name="address"
                placeholder="Address"
                className="buynow-input"
                value={formData.address}
                onChange={handleInputChange}
              />
              <input
                type="text"
                name="apartment"
                placeholder="Apartment, suite, etc. (optional)"
                className="buynow-input"
                value={formData.apartment}
                onChange={handleInputChange}
              />
              <div className="buynow-city-fields">
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  className="buynow-input"
                  value={formData.city}
                  onChange={handleInputChange}
                />
                <input
                  type="text"
                  name="state"
                  placeholder="State"
                  className="buynow-input"
                  value={formData.state}
                  onChange={handleInputChange}
                />
                <div className="pin-wrapper">
                  {/* <input
                    type="text"
                    name="pin"
                    placeholder="PIN code"
                    className="buynow-input"
                    value={pin}
                    maxLength={6}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setPin(val);
                    }}
                  /> */}
                  <input
                    type="text"
                    name="pin"
                    value={pin}
                    placeholder="PIN code"
                    className="buynow-input"
                    maxLength={6}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                      setPin(val);

                      // ✅ ALSO update formData
                      setFormData((f) => ({
                        ...f,
                        pin: val
                      }));
                    }}
                  />
                  {pin.length > 0 && pin.length < 6 && (
                    <p className="pin-msg error">
                      Please enter valid 6 digit pincode
                    </p>
                  )}

                  {pinStatus === "success" && (
                    <p className="pin-msg success">
                      ✅ Delivery available at this pincode
                    </p>
                  )}

                  {pinStatus === "error" && (
                    <p className="pin-msg error">
                      ❌ Delivery not available at this pincode
                    </p>
                  )}
                </div>
              </div>
           
              <div className="buynow-checkbox-wrap">
                <input type="checkbox" id="save-info" />
                <label htmlFor="save-info">Save this information for next time</label>
              </div>
            </div>

            <h4 className="buynow-section-title">Shipping method</h4>
            <div className="buynow-shipping-method-info">
              <p><b>Standard Shipping</b></p>
              <p>FREE</p>
            </div>

            {/* Payment Section */}
            <h2 className="buynow-section-title">Payment</h2>
            <p className="buynow-subtext">All transactions are secure and encrypted.</p>
            {/* <div className="buynow-payment-methods">
              {["cashfree", "cod", "store"].map((method) => (
                <label key={method} className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value={method}
                    checked={paymentMethod === method}
                    onChange={() => setPaymentMethod(method)}
                  />
                  {method === "cashfree"
                    ? "Cashfree Secure (UPI, Cards, Wallets, NetBanking)"
                    : method === "cod"
                      ? "GoKwik Cash on Delivery"
                      : "In-Store Billing"}
                  {paymentMethod === method && method === "cashfree" && (
                    <div className="payment-extra">
                      <div className="payment-icons">
                        <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="MasterCard" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/5/55/RuPay.svg" alt="RuPay" />
                        <img src="https://upload.wikimedia.org/wikipedia/commons/c/ca/UPI_Logo.svg" alt="UPI" />
                      </div>
                      <div className="payment-desc">
                        <img src="/assets/payment-gateway.png" alt="Payment gateway" />
                        <p>After clicking “Pay now”, you’ll be redirected to Cashfree to complete your purchase securely.</p>
                      </div>
                    </div>
                  )}
                </label>
              ))}
            </div> */}
            <div className="buynow-payment-methods">

              {/* ✅ CASHFREE OPTION */}
              <label className="payment-option active-option">
                <input
                  type="radio"
                  name="payment"
                  value="cashfree"
                  checked={paymentMethod === "cashfree"}
                  onChange={() => setPaymentMethod("cashfree")}
                />

                <span className="payment-title">
                 Online Payment
                </span>

                {paymentMethod === "cashfree" && (
                  <div className="payment-extra">

                    {/* Payment Logos */}
                    <div className="payment-icons">
                      <FaCcVisa size={36} />
                      <FaCcMastercard size={36} />
                   
                   
                    </div>

                    <div className="payment-desc">
                      <p>
                        After clicking <b>“Pay Now”</b>, you will be redirected to our secure payment partner
                        to complete your purchase safely.
                      </p>
                    </div>

                  </div>
                )}
              </label>

              {/* 🚚 CASH ON DELIVERY COMING SOON */}
              <div className="payment-coming-soon">
                🚚 Cash on Delivery – <span>Coming Soon</span>
              </div>

            </div>
            {/* <h4 className="buynow-section-title">Billing address</h4>
            <label>
              <input
                type="radio"
                name="billing"
                checked={billingSame}
                onChange={() => setBillingSame(true)}
              />
              Same as shipping address
            </label>
            <label>
              <input
                type="radio"
                name="billing"
                checked={!billingSame}
                onChange={() => setBillingSame(false)}
              />
              Use a different billing address
            </label> */}

            <button
              className="buynow-paynow-button"
              onClick={handlePayNow}
              disabled={
                paying ||
                subtotal <= 0 ||
                availableStock === 0 ||
                pin.length !== 6 ||        // ✅ must be 6 digits
                pinStatus !== "success"    // ✅ must be deliverable
              }
            >
              {paying ? "Processing…" : "Pay now"}
            </button>

            <div className="buynow-policies">
              <a href="#">Refund policy</a>
              <a href="#">Shipping policy</a>
              <a href="#">Privacy policy</a>
              <a href="#">Terms of service</a>
              <a href="#">Contact information</a>
            </div>
          </div>

          <div className="vertical-divider"></div>

          {/* Right Section */}
          <div className="buynow-right-section">
            <div className="buynow-product-summary">
              <img
                src={mainImage}
                alt={product?.productName}
                className="buynow-product-image"
                onError={(e) => (e.currentTarget.src = PLACEHOLDER)}
              />
              {/* <div className="buynow-product-details">
                <h4>{product?.productName}</h4>
                {passedProduct?.size && (
                  <p style={{ fontSize: "14px", marginTop: "4px" }}>
                    Size: <strong>{passedProduct.size}</strong>
                  </p>
                )}
                <p>Unit price: ₹{formatINR(unitPrice)}</p>
                
              </div> */}
              <div className="buynow-product-details">
                <h4>{product?.productName}</h4>

                {/* Variant Selector */}
                {product?.variants?.length > 1 && (
                  <div className="variant-selector" style={{ marginTop: "10px" }}>
                    <h5 style={{ marginBottom: "6px" }}>Select Size</h5>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                      {product.variants.map((v) => (
                        <button
                          key={v.id}
                          type="button"
                          className={`variant-btn ${selectedVariant?.id === v.id ? "active" : ""
                            }`}

                          style={{
                            padding: "6px 12px",
                            border: selectedVariant?.id === v.id
                              ? "2px solid black"
                              : "1px solid #ccc",
                            background:
                              selectedVariant?.id === v.id ? "#f5f5f5" : "white",
                            cursor: "pointer",
                          }}
                          onClick={() => setSelectedVariantId(v.id)}
                        >
                          {v.size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Show selected size */}
                {selectedVariant && (
                  <p style={{ fontSize: "14px", marginTop: "6px" }}>
                    Selected Size: <strong>{selectedVariant.size}</strong>
                  </p>
                )}

                <p style={{ marginTop: "6px" }}>
                  Unit price: ₹{formatINR(unitPrice)}
                </p>
              </div>

            </div>



            <div className="quantity-input-wrap" style={{ marginTop: "8px",textAlign:"left" }}>
              <label htmlFor="quantity" style={{ marginRight: "8px" }}>
                Quantity:
              </label>
              <div className="quantity-control">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => (q > 1 ? q - 1 : 1))}
                  disabled={quantity <= 1}
                >
                  -
                </button>

                <span className="qty-value">{quantity}</span>

                <button
                  type="button"
                  onClick={() =>
                    setQuantity((q) => {
                      if (q >= availableStock) return availableStock;
                      return q + 1;
                    })
                  }
                  disabled={quantity >= availableStock}
                >
                  +
                </button>
              </div>
              {availableStock === 0 ? (
                <p style={{ color: "crimson", fontWeight: 600 }}>
                  ❌ Out of Stock
                </p>
              ) : (
                availableStock <= 5 && (
                  <p style={{ color: "crimson", fontSize: "13px" }}>
                    ⚠ Only {availableStock} left in stock
                  </p>
                )
              )}
            </div>








            {/* <div className="dicountbtn">
              <input
                type="text"
                placeholder="Discount code"
                className="buynow-input discount-input"
              />
              <button className="buynow-apply-button">
                Apply
              </button>
            </div> */}

            <div className={`discount-section ${!isSilverProduct ? "coupon-disabled" : ""}`}>

              {/* Public Coupon Display */}
              <div className={`available-coupon-card ${!isSilverProduct ? "is-disabled" : ""}`}>
                <div className="coupon-left">
                  <span className="coupon-tag">🎉 OFFER</span>
                  <div className="coupon-code">GIFTFORHER</div>
                  <div className="coupon-desc">
                    {isSilverProduct
                      ? "Get 20% OFF on silver jewellery"
                      : "Valid only on silver jewellery"}
                  </div>
                </div>

                <button
                  type="button"
                  className="select-coupon-btn"
                  disabled={!isSilverProduct}
                  onClick={() => {
                    if (!isSilverProduct) return;
                    setDiscountCode("GIFTFORHER");
                    setDiscountPercent(20);
                    setDiscountError("");
                  }}
                >
                  Apply
                </button>
              </div>

              {/* Input */}
              <div className="dicountbtn">
                <input
                  type="text"
                  value={discountCode}
                  placeholder={isSilverProduct ? "Discount code" : "Coupon not valid for this product"}
                  className="buynow-input discount-input"
                  disabled={!isSilverProduct}
                  onChange={(e) => {
                    if (!isSilverProduct) return;
                    setDiscountCode(e.target.value);
                    setDiscountPercent(0); // reset until applied
                  }}
                />
                <button
                  type="button"
                  className="buynow-apply-button"
                  disabled={!isSilverProduct}
                  onClick={handleApplyDiscount}
                >
                  Apply
                </button>
              </div>

              {!isSilverProduct && (
                <p className="coupon-unavailable-msg">
                  Coupon codes apply only to silver products.
                </p>
              )}

              {isSilverProduct && discountPercent > 0 && (
                <p style={{ color: "green", marginTop: "6px" }}>
                  🎉 20% discount applied!
                </p>
              )}

              {isSilverProduct && discountError && (
                <p style={{ color: "crimson", marginTop: "6px" }}>
                  {discountError}
                </p>
              )}

            </div>
            <div className="buynow-pricing-summary">
              {/* <div className="buynow-subtotal">
                <span>Subtotal</span>
                <span>
                  ₹{formatINR(unitPrice)} × {quantity} = {" "}
                  <strong>₹{formatINR(subtotal)}</strong>
                </span>
              </div> */} <div className="buynow-subtotal">
                <span>Subtotal</span>
                <span>₹{formatINR(subtotal)}</span>
              </div>

              {isSilverProduct && discountPercent > 0 && (
                <div className="buynow-discount-line">
                  <span>Discount (20%)</span>
                  <span>- ₹{formatINR(discountAmount)}</span>
                </div>
              )}

              {/* <div className="buynow-total">
                <h3>Total</h3>
                <h3>₹{formatINR(finalTotal)}</h3>
              </div> */}
              <div className="buynow-total">
                <h3>Total</h3>
                <h3>₹{formatINR(finalTotal)}</h3>
              </div>
              <div className="buynow-shipping-estimate">
                <p className="live-data">
                  <strong>Deliver to:</strong> {formData.firstName} {formData.lastName}, {formData.address}, {formData.city}, {formData.state} - {formData.pin}
                  <br />
                  <strong>Contact:</strong> {formData.email}, {formData.phone}
                </p>
              </div>

              {/* <div className="buynow-total">
                <h3>Total</h3>
                <h3>₹{formatINR(subtotal)}</h3>
              </div> */}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default BuyNowPage;
